import { BybAvatar } from "@Components/common/byb-ds";
import { surfaceV100Background } from "@Components/common/byb-ds/buttons/styled-components";
import { AccountPageLayout } from "@Components/game/account";
import { BybButtonVR, BybMenuItem, BybTextFieldAlt } from "@Components/game/common";
import CenteredLoadingOverlay from "@Components/generic/centered-loading-overlay";
import Icon from "@Components/generic/icon";
import { Events } from "@Context/common/general/context";
import VRHeaderButtonType from "@Enums/VRHeaderButtonType";
import { faXmark } from "@fortawesome/pro-solid-svg-icons";
import { useApi } from "@hooks";
import useGlobalContext from "@hooks/common/general/use-global-context";
import useI18n from "@i18n";
import { Alert, Box, CircularProgress, FormControl, IconButton, InputAdornment, InputLabel, Stack, Typography } from "@mui/material";
import { closeKeyboard } from "@Services/keyboard-service";
import AccountRoles from "@Shared/enums/AccountRoles";
import { Api } from "@Shared/schemas";
import { RoleRecord } from "@Shared/schemas/accounts/accountSchemas";
import { InviteResponse } from "@Shared/schemas/accounts/inviteSchemas";
import { UserRecord } from "@Shared/schemas/users/userSchemas";
import { getRoleLevel } from "@Shared/utils/rolePermissions";
import { capitalize } from "lodash-es";
import { useEffect, useState } from "react";
import { isEmail } from "validator";

const SEARCH_DEBOUNCE_MS = 1000;

// Reference to the debounce timer - hoisted for simplicity
let debounceTimer: any = null;

const InviteUserContent = () => {
    const { i18n } = useI18n();
    const { globalState } = useGlobalContext();
    const [email, setEmail] = useState("");
    const [roles, setRoles] = useState<RoleRecord[]>([]);
    const [selectedRole, setSelectedRole] = useState<RoleRecord | null>(null);
    const [foundUser, setFoundUser] = useState<UserRecord | null>(null);
    const [isValidEmail, setIsValidEmail] = useState(false);
    const [searchComplete, setSearchComplete] = useState(false);
    const [isExistingMember, setIsExistingMember] = useState(false);
    const [pendingInvites, setPendingInvites] = useState<InviteResponse[]>([]);
    const [isPendingInvite, setIsPendingInvite] = useState(false);
    const [accountsData, setAccountsData] = useState<any>(null);

    const isSearchLoading = useApiLoading([Api.Account.SearchUsers]);

    // Get available roles 
    const { call: getRoles } = useApi({
        api: Api.Account.GetRoles,
        onSuccess: ({ data }) => {
            if (data && globalState.account?.user_role) {
                const currentUserRoleLevel = getRoleLevel(globalState.account.user_role);
                // Filter roles to only include those below the current user's role level
                const availableRoles = data.filter(role => {
                    const roleEnum = role.name as AccountRoles;
                    return getRoleLevel(roleEnum) < currentUserRoleLevel;
                });
                // Sort roles by hierarchy level (highest first)
                const sortedRoles = availableRoles.sort((a, b) => {
                    const aLevel = getRoleLevel(a.name as AccountRoles);
                    const bLevel = getRoleLevel(b.name as AccountRoles);
                    return bLevel - aLevel;
                });
                setRoles(sortedRoles);
                // Set default role (e.g., Member)
                const defaultRole = sortedRoles.find(role => role.name === AccountRoles.MEMBER);
                if (defaultRole) {
                    setSelectedRole(defaultRole);
                }
            }
        }
    });

    // Get current account members
    const { call: getAccounts } = useApi({
        api: Api.Account.GetAccounts,
        request: {
            resolve_members: true,
            account_ids: [globalState.account.id!]
        },
        onSuccess: ({ data }) => {
            setAccountsData(data);
        }
    });

    // Get pending invites for the account
    const { call: getInvitesForAccount } = useApi({
        api: Api.Invites.GetInvitesForAccount,
        request: {
            account_id: globalState.account.id!
        },
        onSuccess: ({ data }) => {
            // Filter to only pending invites
            const pending = data.filter(invite => invite.state === 'pending');
            setPendingInvites(pending);
        }
    });

    // Search for existing user
    const { call: searchUsers } = useApi({
        api: Api.Account.SearchUsers,
        onSuccess: ({ data }) => {
            if (data.length > 0) {
                const matchingUser = data[0]; // We're using limit: 1, so we only get exact matches
                
                // Check if user is already a member
                const isMember = accountsData && accountsData[0].members.some(
                    (member: any) => member.user_id === matchingUser.id
                );
                
                // Check if user already has a pending invite
                const isPending = pendingInvites.some(invite => 
                    (invite.recipient_user_id && invite.recipient_user_id === matchingUser.id) || 
                    invite.email.toLowerCase() === email.toLowerCase()
                );
                
                setIsExistingMember(isMember);
                setIsPendingInvite(isPending);
                setFoundUser(matchingUser);
            } else {
                // For users not found in the system, check if the email has a pending invite
                const isPending = pendingInvites.some(invite => 
                    invite.email.toLowerCase() === email.toLowerCase()
                );
                
                setFoundUser(null);
                setIsExistingMember(false);
                setIsPendingInvite(isPending);
            }
            setSearchComplete(true);
        }
    });

    // Create invite
    const { call: createInvite, loading: createInviteLoading } = useApi({
        api: Api.Invites.CreateInvites,
        onSuccess: () => {
            Events.account.refreshCurrentAccount.emit();
            Events.app.navigateBack.emit();
        }
    });


    const handleEmailChange = (value: string) => {
        setEmail(value);
        const isValid = isEmail(value);
        setIsValidEmail(isValid);
        setSearchComplete(false);
        setFoundUser(null);
        
        if (isValid) {
            if (debounceTimer) {
                clearTimeout(debounceTimer);
                debounceTimer = null;
            }
            debounceTimer = setTimeout(() => {
                debounceTimer = null;
                searchUsers({
                    search_query: value,
                    limit: 1
                });
            }, SEARCH_DEBOUNCE_MS);
        } else {
            setIsPendingInvite(false);
        }
    };

    const handleClearUser = () => {
        setEmail("");
        setFoundUser(null);
        setIsValidEmail(false);
        setSearchComplete(false);
        setIsExistingMember(false);
        setIsPendingInvite(false);
    };

    const handleCancel = () => {
        Events.app.navigateBack.emit();
    };

    const handleSubmit = async () => {
        if (!isValidEmail || !selectedRole) return;

        await createInvite({
            account_id: globalState.account.id!,
            invites: [{
                email,
                ...(foundUser?.id && { recipient_user_id: foundUser.id }),
                role_id: selectedRole?.id
            }]
        });
    };

    useEffect(() => {
    Events.app.showHeaderButton.emit({ type: VRHeaderButtonType.BACK, title: i18n("account.profile.invite.title") });
        Events.app.setHeaderAltSections.emit({
            centerSection: <></>,
            rightSection: <></>,
        });
        getRoles();
        getAccounts();
        getInvitesForAccount();

        return () => {
      Events.app.hideHeaderButton.emit();
            Events.app.setHeaderAltSections.emit(null);
            closeKeyboard();
            clearTimeout(debounceTimer);
            debounceTimer = null;
        };
    }, []);

    return (
        <AccountPageLayout
            pageId="INVITE_USER"
            title={i18n("account.profile.invite.title")}
            // subtitle={globalState.account.name!}
            description={i18n("account.profile.invite.subtitle")}
            sx={{ height: "100%" }}
        >
        <Stack sx={{ height: "100%", justifyContent: "space-between", alignItems: "center" }}>
            <Stack spacing={3} sx={{ maxWidth: "480px", width: "100%" }}>
            {createInviteLoading && <CenteredLoadingOverlay loading={createInviteLoading} />}
            {foundUser ? (
                <>
                    <FormControl fullWidth>
                        <InputLabel 
                            sx={{ 
                                position: 'relative', 
                                textAlign: 'left',
                                transform: 'none',
                                marginBottom: 1,
                                marginLeft: 0.5
                            }}
                        >
                            {i18n("account.profile.invite.found.label")}
                        </InputLabel>
                        <Box
                            sx={(theme) => ({
                                width: "100%",
                                ...surfaceV100Background(theme),
                                padding: theme.spacing(2.5),
                                borderRadius: theme.spacing(2),
                                position: "relative",
                                boxSizing: "border-box"
                            })}
                        >
                            <Stack direction="row" spacing={2.5} sx={{ width: "100%", alignItems: "center"}}>
                                <BybAvatar src={foundUser.avatar || undefined} size={8} user={foundUser} />
                                <Stack spacing={0.5} sx={{ flexGrow: 1, textAlign: "left" }}>
                                    <Typography variant="text1xl600" color="dsBase.v200">
                                        {`${capitalize(foundUser.first_name)} ${capitalize(foundUser.last_name)}`}
                                    </Typography>
                                    <Typography variant="textLg400" color="dsNeutral.v300">
                                        {foundUser.email}
                                    </Typography>
                                </Stack>
                                <IconButton 
                                    onClick={handleClearUser}
                                    sx={{
                                        width: "40px",
                                        height: "40px",
                                    }}
                                >
                                    <Icon 
                                        icon={faXmark} 
                                        fontSize={24} 
                                        sx={{ 
                                            color: "dsNeutral.v300",
                                            "&:hover": {
                                                color: "dsNeutral.v200"
                                            }
                                        }} 
                                    />
                                </IconButton>
                            </Stack>
                        </Box>
                    </FormControl>
                    <Alert 
                        severity={
                            isExistingMember 
                                ? "warning" 
                                : isPendingInvite 
                                    ? "warning" 
                                    : "info"
                        } 
                        sx={{ borderRadius: 2 }}
                    >
                        {isExistingMember 
                            ? i18n("account.profile.invite.alert.existing.member")
                            : isPendingInvite 
                                ? i18n("account.profile.invite.alert.pending")
                                : i18n("account.profile.invite.alert.existing")}
                    </Alert>
                </>
            ) : (
                <>
                    <BybTextFieldAlt
                        id="email"
                        name="email"
                        label={i18n("account.profile.invite.email.label")}
                        placeholder="email@example.com"
                        value={email}
                        autoComplete={"off"}
                        onChange={(e) => handleEmailChange(e.target.value)}
                        fullWidth
                        InputProps={{
                            startAdornment: isSearchLoading ? (
                                <InputAdornment position="end">
                                    <CircularProgress size={20} />
                                </InputAdornment>
                            ) : null
                        }}
                    />
                    {isValidEmail && searchComplete && !isSearchLoading && !foundUser && !isExistingMember && (
                        <Alert 
                            severity={isPendingInvite ? "warning" : "info"} 
                            sx={{ borderRadius: 2 }}
                        >
                            {isPendingInvite 
                                ? i18n("account.profile.invite.alert.pending") 
                                : i18n("account.profile.invite.alert.new")}
                        </Alert>
                    )}
                </>
            )}

            {roles.length > 0 && (
                <BybTextFieldAlt
                    name="role"
                    value={selectedRole?.name || ""}
                    onChange={(e) => setSelectedRole(roles.find(role => role.name === e.target.value) || null)}
                    fullWidth
                    select
                    label={i18n("account.profile.invite.role.label")}
                >
                    {roles.map((role) => (
                        <BybMenuItem key={role.id} value={role.name}>
                        {i18n(`roles.${role.name}`)}
                        </BybMenuItem>
                    ))}
                </BybTextFieldAlt>
            )}
            </Stack>

            <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: "auto", pt: 4 }}>
            <BybButtonVR color="secondary" onClick={handleCancel}>
                {i18n("account.profile.invite.button.cancel")}
            </BybButtonVR>
            <BybButtonVR
                color="primary"
                onClick={handleSubmit}
                disabled={!isValidEmail || !searchComplete || !selectedRole || isExistingMember || isPendingInvite}
            >
                {i18n("account.profile.invite.button.submit")}
            </BybButtonVR>
            </Stack>
        </Stack>
        </AccountPageLayout>
    );
};

const InviteUser = () => {
    return <InviteUserContent />;
};

export default InviteUser; 